/* Maps a notification's semantic severity onto Stencil icons and design tokens. */
import IconAlertTriangleFillMedium from '@amzn/stencil-react-icons/icons/icon-alert-triangle-fill-medium';
import IconCheckCircleFillMedium from '@amzn/stencil-react-icons/icons/icon-check-circle-fill-medium';
import IconClockFillMedium from '@amzn/stencil-react-icons/icons/icon-clock-fill-medium';
import IconCloseCircleFillMedium from '@amzn/stencil-react-icons/icons/icon-close-circle-fill-medium';
import { NotificationSeverity } from './notificationPresentation';

export interface SeverityStencilPresentation {
  /** Leading icon component (24px medium fill icon). */
  Icon: typeof IconCheckCircleFillMedium;
}

/**
 * One entry per severity, distinguished by icon SHAPE rather than color.
 *
 * Stencil's status colors (teal-green, brick red, blue, mustard) are a different palette
 * from the Cloudscape one the rest of the app uses, so four of them stacked down a list
 * read as arbitrary rather than meaningful. A tick, a cross, a clock and a warning triangle
 * carry the same information without introducing a second palette — and they stay legible
 * for anyone who cannot distinguish the hues anyway.
 */
const SEVERITY_PRESENTATION: Record<NotificationSeverity, SeverityStencilPresentation> = {
  success: { Icon: IconCheckCircleFillMedium },
  critical: { Icon: IconCloseCircleFillMedium },
  // A clock reads as "waiting on someone" more directly than a person glyph does.
  pending: { Icon: IconClockFillMedium },
  unknown: { Icon: IconAlertTriangleFillMedium }
};

/** Stencil icon for a notification severity. */
export const getSeverityStencilPresentation = (severity: NotificationSeverity): SeverityStencilPresentation =>
  SEVERITY_PRESENTATION[severity];

/**
 * Single icon color for every row: the same dark neutral the row title uses.
 *
 * Held here rather than per severity so a future change cannot reintroduce a per-row
 * palette by accident.
 */
export const ICON_COLOR = 'color.icon.primary-default';

/**
 * Width of the leading icon column.
 *
 * Fixed so every row's text starts on the same vertical line regardless of which icon
 * it carries. Stencil publishes spacing, border and z-index dimension tokens but no
 * sizing tokens, so a raw value is the sanctioned fallback for a width.
 */
export const ICON_COLUMN_WIDTH = '20px';
